import { useCallback, useEffect, useState } from "react";
import { inventoryFetchClient } from "./inventoryFetchClient";
import type { InventoryCatalogEntry } from "./types";
import { useStableOnError } from "./useStableOnError";

type UseGetInventoryCatalogOptions = {
  skip?: boolean;
  onError?: (error: Error) => void;
};

export const useGetInventoryCatalog = (
  options?: UseGetInventoryCatalogOptions
) => {
  const onError = useStableOnError(options?.onError);
  const skip = Boolean(options?.skip);
  const [data, setData] = useState<InventoryCatalogEntry[] | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (skip) {
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await inventoryFetchClient<InventoryCatalogEntry[]>(
        "/catalog",
        {
          method: "GET",
          operation: "getInventoryCatalog",
          onError,
        }
      );
      setData(result ?? []);
      return result ?? [];
    } catch (err) {
      const nextError =
        err instanceof Error ? err : new Error("Failed to load catalog");
      setError(nextError);
      setData(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [skip, onError]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
};
